import React, { useState, useEffect, useCallback } from "react"
import { Form, Button, Spinner } from "react-bootstrap"
import useAuthAxios from "../../hooks/useAuthAxios"
import { toast } from "react-toastify"
import DateTimePicker from "../common/DateTimePicker"
import { formatDateForApi, formatDateForDisplay } from "../../utils/dateUtils"

const OutOfOfficeForm = ({ expertId, initialData, disabled }) => {
  const { get, post } = useAuthAxios()
  const [dates, setDates] = useState({
    DateTimeFrom: null,
    DateTimeTo: null
  })
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [validationErrors, setValidationErrors] = useState({
    DateTimeFrom: null,
    DateTimeTo: null,
    general: null
  })

  // Separate validation function
  const validateDates = useCallback(datesToValidate => {
    const newValidationErrors = {
      DateTimeFrom: null,
      DateTimeTo: null,
      general: null
    }
    const now = new Date()

    if (datesToValidate.DateTimeFrom || datesToValidate.DateTimeTo) {
      // Check for incomplete pairs
      if (!datesToValidate.DateTimeFrom || !datesToValidate.DateTimeTo) {
        newValidationErrors.general =
          "Παρακαλώ συμπληρώστε και τις δύο ημερομηνίες ή αφήστε τες και τις δύο κενές"
      } else {
        const startDate = new Date(datesToValidate.DateTimeFrom)
        const endDate = new Date(datesToValidate.DateTimeTo)

        // Check for past dates
        if (startDate < now) {
          newValidationErrors.DateTimeFrom =
            "Η ημερομηνία έναρξης (" + formatDateForDisplay(startDate) + ") δεν μπορεί να είναι στο παρελθόν"
        }
        if (endDate < now) {
          newValidationErrors.DateTimeTo =
            "Η ημερομηνία λήξης (" + formatDateForDisplay(endDate) + ") δεν μπορεί να είναι στο παρελθόν"
        }

        // Check date order
        if (startDate >= endDate) {
          newValidationErrors.DateTimeTo =
            "Η ημερομηνία λήξης πρέπει να είναι μεταγενέστερη της ημερομηνίας έναρξης"
        }
      }
    }

    setValidationErrors(newValidationErrors)
  }, [])

  useEffect(() => {
    const fetchOutOfOffice = async () => {
      if (!expertId) return
      if (initialData) {
        setDates({
          DateTimeFrom: initialData.DateTimeFrom || "",
          DateTimeTo: initialData.DateTimeTo || ""
        })
        return
      }

      setLoading(true)
      try {
        const response = await get(`/expert/${expertId}/outOfOffice`)
        if (response.success && response.data) {
          const newDates = {
            DateTimeFrom: response.data.DateTimeFrom || "",
            DateTimeTo: response.data.DateTimeTo || ""
          }
          setDates(newDates)

          // Validate the fetched dates
          validateDates(newDates)
        }
      } catch (err) {
        console.error("Error fetching out of office dates:", err)
        toast.error("Αποτυχία φόρτωσης περιόδων εκτός λειτουργίας")
      } finally {
        setLoading(false)
      }
    }

    fetchOutOfOffice()
  }, [expertId, initialData, get, validateDates])

  const handleDateChange = (field, value) => {
    // Update state first
    const newDates = {
      ...dates,
      [field]: value
    }
    setDates(newDates)

    // Validate the new dates
    validateDates(newDates)
  }

  const checkPendingInspections = async (from, to) => {
    try {
      const response = await get("/inspections", {
        params: {
          ExpertID: expertId,
          Status: [{ short: 0 }], // Pending inspections
          ExpertAssignmentDateFrom: from,
          ExpertAssignmentDateTimeTo: to
        }
      })

      // If response.data is an array and has items, there are conflicts
      if (Array.isArray(response.data) && response.data.length > 0) {
        const inspectionsCount = response.data.length
        setValidationErrors(prev => ({
          ...prev,
          general: `Cannot set out of office dates: You have ${inspectionsCount} pending inspection${
            inspectionsCount > 1 ? "s" : ""
          } in this period. Please reassign or complete them first.`
        }))
        return false
      }

      // If we get here, either there are no inspections (success: false with "no records")
      // or we got an empty array (success: true with no items)
      // Both cases mean no conflicts
      setValidationErrors(prev => ({ ...prev, general: null }))
      return true
    } catch (err) {
      console.error("Error checking pending inspections:", err)
      toast.error("Failed to check pending inspections. Please try again.")
      return false
    }
  }

  const handleSubmit = async e => {
    e.preventDefault()

    // Both empty is valid, but only one set is not
    if ((!dates.DateTimeFrom && dates.DateTimeTo) || (dates.DateTimeFrom && !dates.DateTimeTo)) {
      setValidationErrors(prev => ({
        ...prev,
        general: "Παρακαλώ συμπληρώστε και τις δύο ημερομηνίες ή αφήστε τες και τις δύο κενές"
      }))
      return
    }

    // If both dates are set, validate their order
    if (dates.DateTimeFrom && dates.DateTimeTo) {
      const startDate = new Date(dates.DateTimeFrom)
      const endDate = new Date(dates.DateTimeTo)

      if (startDate >= endDate) {
        setValidationErrors(prev => ({
          ...prev,
          DateTimeTo: "Η ημερομηνία λήξης πρέπει να είναι μεταγενέστερη της ημερομηνίας έναρξης"
        }))
        return
      }
    }

    setSaving(true)

    try {
      // Check for pending inspections before saving only if both dates are set
      if (dates.DateTimeFrom && dates.DateTimeTo) {
        const canProceed = await checkPendingInspections(dates.DateTimeFrom, dates.DateTimeTo)
        if (!canProceed) {
          setSaving(false)
          return
        }
      }

      // Only include dates that are not null
      const formattedDates = {}
      if (dates.DateTimeFrom) formattedDates.DateTimeFrom = formatDateForApi(dates.DateTimeFrom)
      if (dates.DateTimeTo) formattedDates.DateTimeTo = formatDateForApi(dates.DateTimeTo)

      const response = await post(`/expert/${expertId}/outOfOffice`, formattedDates)

      if (response.success && response.data?.IsSuccess) {
        // Dismiss any existing success toasts
        toast.dismiss("outOfOfficeError")
        toast.success("Οι περίοδοι εκτός λειτουργίας αποθηκεύτηκαν επιτυχώς", { autoClose: 3000 })
      } else {
        toast.error(response.data?.Problem || "Αποτυχία αποθήκευσης περιόδων εκτός λειτουργίας", {
          toastId: "outOfOfficeError"
        })
      }
    } catch (err) {
      console.error("Error saving out of office dates:", err)
      toast.error("Αποτυχία αποθήκευσης περιόδων εκτός λειτουργίας", {
        toastId: "outOfOfficeError"
      })
    } finally {
      setSaving(false)
    }
  }

  const handleClear = field => {
    const newDates = {
      ...dates,
      [field]: null
    }
    setDates(newDates)

    // Keep existing validation errors but clear the current field's error
    const newValidationErrors = { ...validationErrors }
    newValidationErrors[field] = null

    // Check for incomplete pairs after clearing
    if ((newDates.DateTimeFrom && !newDates.DateTimeTo) || (!newDates.DateTimeFrom && newDates.DateTimeTo)) {
      newValidationErrors.general =
        "Παρακαλώ συμπληρώστε και τις δύο ημερομηνίες ή αφήστε τες και τις δύο κενές"
    } else {
      newValidationErrors.general = null
    }

    setValidationErrors(newValidationErrors)
  }

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <div className="mt-2 text-muted">Φόρτωση περιόδων εκτός λειτουργίας...</div>
      </div>
    )
  }

  return (
    <Form onSubmit={handleSubmit}>
      <div className="row g-4">
        <div className="col-md-6">
          <DateTimePicker
            label="Από"
            value={dates.DateTimeFrom}
            onChange={value => handleDateChange("DateTimeFrom", value)}
            disabled={disabled || saving}
            isInvalid={!!validationErrors.DateTimeFrom}
            onClear={() => handleClear("DateTimeFrom")}
            minDate={new Date()}
          />
          {validationErrors.DateTimeFrom && (
            <div className="text-danger small mt-1">
              <i className="fas fa-exclamation-circle me-1" />
              {validationErrors.DateTimeFrom}
            </div>
          )}
        </div>
        <div className="col-md-6">
          <DateTimePicker
            label="Έως"
            value={dates.DateTimeTo}
            onChange={value => handleDateChange("DateTimeTo", value)}
            disabled={disabled || saving}
            isInvalid={!!validationErrors.DateTimeTo}
            onClear={() => handleClear("DateTimeTo")}
            minDate={new Date()}
          />
          {validationErrors.DateTimeTo && (
            <div className="text-danger small mt-1">
              <i className="fas fa-exclamation-circle me-1" />
              {validationErrors.DateTimeTo}
            </div>
          )}
        </div>
      </div>
      {validationErrors.general && (
        <div className="alert alert-danger d-flex align-items-center mt-4">
          <i className="fas fa-exclamation-circle me-2" />
          {validationErrors.general}
        </div>
      )}
      {!disabled && (
        <div className="d-flex justify-content-end mt-4">
          <Button
            type="submit"
            variant="primary"
            disabled={
              saving ||
              (dates.DateTimeFrom && !dates.DateTimeTo) ||
              (!dates.DateTimeFrom && dates.DateTimeTo) ||
              !!validationErrors.DateTimeFrom ||
              !!validationErrors.DateTimeTo ||
              !!validationErrors.general
            }>
            {saving ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Αποθήκευση...
              </>
            ) : (
              <>
                <i className="fas fa-save me-2" />
                Αποθήκευση
              </>
            )}
          </Button>
        </div>
      )}
    </Form>
  )
}

export default OutOfOfficeForm
